import {
  ApolloCache,
  DefaultContext,
  LazyQueryHookOptions,
  MutationFunctionOptions,
  OperationVariables,
} from '@apollo/client'
import { DEFAULT_ERROR_MESSAGE } from 'appconstants/AppStrings'
import { ApiError } from 'models'
import { setError, setLoadingData } from 'redux/reducers/global'
import { isObjectEmpty } from 'routes/Pages/utils'
import { ErrorMessages } from 'utils/api/errormessages'

export const DEFAULT_ERROR: ApiError = {
  type: 'Oops!',
  message: DEFAULT_ERROR_MESSAGE,
  code: 'OOPS!',
}

type MutateFnOptions = MutationFunctionOptions<
  any,
  OperationVariables,
  DefaultContext,
  ApolloCache<any>
>

type LazyQueryFnOptions = Partial<LazyQueryHookOptions<any, OperationVariables>> | undefined

// TODO: Add type `ApiError`
/**
 * @deprecated
 *
 * Calls the given GQL query with the given input parameters.
 *
 * Use `executeGqlOperation` instead.
 * @see executeGqlOperation
 */
export default async function gqlCaller<T = any>({
  gqlQuery,
  dispatch,
  inputParams,
  apiMessagePreferredErrorTypes = [],
}: {
  gqlQuery
  dispatch
  inputParams
  apiMessagePreferredErrorTypes?: string[]
  showLoader?: boolean // TODO: Remove.
  queryId?: number // TODO: Remove.
}): Promise<{ data?: T; error: any }> {
  let error
  dispatch(setLoadingData(true))
  try {
    const result = await gqlQuery(inputParams)
    return {
      data: isObjectEmpty(result?.data) ? null : result?.data,
      error: extractErrorData(result?.error, apiMessagePreferredErrorTypes),
    }
  } catch (e) {
    error = extractErrorData(e, apiMessagePreferredErrorTypes)
    if (error.message === undefined) {
      error.message = DEFAULT_ERROR_MESSAGE
    }
    dispatch(setError(error))
    return { error }
  } finally {
    dispatch(setLoadingData(false))
  }
}

/**
 * Extracts error data from the given GQL error object.
 *
 * @param {Object} error - The error object.
 * @return {ApiError} The extracted error data with `type`, `details`, and `message`
 */
export const extractErrorData = (error, apiMessagePreferredErrorTypes: string[] = []) => {
  // TODO: Add type to param.
  // TODO: Add return type.

  if (!error) {
    return
  }

  const { message = '', type = '', code = '' } = error?.graphQLErrors[0]?.extensions || {}

  if (type && ErrorMessages[type]) {
    return {
      type,
      message:
        apiMessagePreferredErrorTypes.includes(type) && message
          ? message
          : ErrorMessages[type],
      code: code || DEFAULT_ERROR.code,
    }
  }
  // PDS-112 error comes in revisit case
  // khushal had no way to skip INTERNAL_SERVER_ERROR for this case
  // so added a condition to skip it

  if (code === 'INTERNAL_SERVER_ERROR' && type != 'PDS-112' && type != 'ODS-216') {
    return DEFAULT_ERROR
  }

  return {
    type: type || DEFAULT_ERROR.type,
    message: message ? message : error?.graphQLErrors[0]?.message || DEFAULT_ERROR.message,
    code,
  }
}

/**
 * A utility function to make GraphQL queries or mutations with version 3 configuration.
 *
 * This version differs from v2 (gqlCallerV2) in that it accepts variables directly instead of bundling them within an 'input' object.
 *
 * Also, this enforces strict type for response.
 */
export const executeGqlOperation = async <T>({
  operation,
  variables,
  options = { context: { isAuthRequired: true } },
}: {
  operation: any
  variables: OperationVariables
  options?: MutateFnOptions | LazyQueryFnOptions
}): Promise<{ data?: T; error: ApiError }> => {
  try {
    const { data, error } = await operation({ variables, ...options })
    const formattedError = extractErrorData(error)
    return { data, error: formattedError }
  } catch (error) {
    const formattedError = extractErrorData(error)
    if (!formattedError.message) {
      formattedError.message = DEFAULT_ERROR_MESSAGE
    }
    return { error: formattedError }
  }
}

/**
 * A utility function to make GraphQL queries or mutations with version 2 configuration.
 *
 * If you'd like to pass variables directly, use `executeGqlOperation` instead.
 * @see executeGqlOperation
 */
export const gqlCallerV2 = <T = any>({ gqlQuery, dispatch, input, isAuthRequired = true }) =>
  gqlCaller<T>({
    gqlQuery,
    dispatch,
    inputParams: {
      variables: { input },
      context: { isAuthRequired },
    },
    queryId: 999,
  })
